import React, { ReactElement, useState, useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CheckmarkSquareIcon from '../../../../assets/icons/CheckmarkSquareIcon';
import HighPriorityIcon from '../../../../assets/icons/HighPriorityIcon';
import CheckBoxSpinner from '../../../layout/Spinner/CheckBoxSpinner';

import { APIPickTour } from '../../../../redux/sagas/types';
import { selectCurrentCountry } from '../../../../redux/selectors';

import {
  mergeClassNames,
  displayOrderType,
  determineIconByOrderType,
  displayDestination,
  displayDate,
  epRegEx,
} from '../../../../utils';
import { isBundledCommercialWithEditOrder } from '../../../../utils/pickHelpers';
import { LANGUAGE_SPANISH, ORDER_TYPE_COMMERCIAL_EP } from '../../../../constants';

import styles from './PickTourItem.css';
import CommercialEPDispatchTimeDisplay from '../CommercialEPDispatchTimeDisplay';
import { findEarliestDispatchTime } from '../../../../utils/dispatchTime';

type Props = {
  pickTour: APIPickTour;
  isSelected: boolean;
  onSelect: () => void;
  currentLanguage: string;
  verifying?: boolean;
  editOrderEnabled?: boolean;
  onOrderSelectionChange?: (pickTourID: string, orderIDs: string[]) => void;
  pickTourID?: string;
};

const PickTourItem = ({
  pickTour,
  isSelected,
  onSelect,
  currentLanguage,
  verifying,
  editOrderEnabled = false,
  onOrderSelectionChange,
  pickTourID,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const translationRoot = 'ORDER_LIST.LIST_ITEM';
  const countryCode = useSelector(selectCurrentCountry);
  const [datePart = '', timePart = ''] = (displayDate({ date: pickTour.createTS, countryCode }) || '').split('-');
  const pickTourHasNoItems = pickTour.pickTourList.length === 0;
  const pickTourOrderType = pickTourHasNoItems ? pickTour.pickTourType : pickTour.pickTourList[0].orderType;

  const isSelectiveResumeEnabled = isBundledCommercialWithEditOrder(pickTour, editOrderEnabled);

  // Initialize with all orders selected
  const [selectedOrderIDs, setSelectedOrderIDs] = useState<Set<string>>(
    new Set(pickTour.metadata.ordersSummaryData.map(order => order.orderID))
  );

  const prevIsSelectedRef = useRef(isSelected);
  const isResettingRef = useRef(false);

  // When parent checkbox is re-selected, reset all orders to selected
  useEffect(() => {
    if (isSelectiveResumeEnabled && isSelected && !prevIsSelectedRef.current) {
      // Parent was just selected - reset all orders to selected
      isResettingRef.current = true;
      setSelectedOrderIDs(new Set(pickTour.metadata.ordersSummaryData.map(order => order.orderID)));
      // Clear the resetting flag after state update
      setTimeout(() => {
        isResettingRef.current = false;
      }, 0);
    }
    prevIsSelectedRef.current = isSelected;
  }, [isSelected, isSelectiveResumeEnabled, pickTour.metadata.ordersSummaryData]);

  // Auto-deselect parent when all children are manually deselected
  useEffect(() => {
    // Don't auto-deselect if we're in the middle of resetting
    if (isSelectiveResumeEnabled && isSelected && selectedOrderIDs.size === 0 && !isResettingRef.current) {
      // All orders deselected - deselect parent pick tour
      onSelect();
    }
  }, [selectedOrderIDs, isSelectiveResumeEnabled, isSelected, onSelect]);

  // Notify parent when selection changes
  useEffect(() => {
    if (isSelectiveResumeEnabled && isSelected && onOrderSelectionChange && pickTourID) {
      onOrderSelectionChange(pickTourID, Array.from(selectedOrderIDs));
    }
  }, [isSelected, isSelectiveResumeEnabled, onOrderSelectionChange, selectedOrderIDs, pickTourID]);

  const getDestination = (): string | null => {
    if (pickTour.metadata.ordersSummaryData.length > 0 && pickTour.metadata.ordersSummaryData[0].destination) {
      const { destination } = pickTour.metadata.ordersSummaryData[0];
      return destination;
    }
    return null;
  };

  const pickTourDestination = getDestination();

  function determinePickTourType() {
    let destination = '';
    if (pickTour.metadata.ordersSummaryData.length > 0 && pickTour.metadata.ordersSummaryData[0].destination) {
      destination = pickTour.metadata.ordersSummaryData[0].destination;
      destination =
        String(Number(destination)) === 'NaN'
          ? ` | ${destination}`
          : ` | ${String(Number(destination)).padStart(5, '0')}`;
    }
    return `${t(`${translationRoot}.RESUME`)}${destination}`;
  }

  function getCommercialEPOrderDispatchTime() {
    if (pickTour.metadata.ordersSummaryData.length > 0) {
      const allDispatchTimes: string[] = [];

      pickTour.metadata.ordersSummaryData.forEach(order => {
        if (order.dispatchTimes) {
          allDispatchTimes.push(...order.dispatchTimes);
        }
      });

      return findEarliestDispatchTime(allDispatchTimes);
    }
    return null;
  }

  const toggleOrderSelection = useCallback((orderID: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedOrderIDs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderID)) {
        newSet.delete(orderID);
      } else {
        newSet.add(orderID);
      }
      return newSet;
    });
  }, []);

  return (
    <li className={styles.li} onClick={() => onSelect()} data-cy={`pickTour-list-item-${pickTour.pickTourID}`}>
      <div className={styles.pickInfo}>
        {verifying ? (
          <div className={styles.checkboxArea}>
            <CheckBoxSpinner />
          </div>
        ) : (
          <div className={styles.checkboxArea}>
            {isSelected ? <CheckmarkSquareIcon /> : <div className={styles.circle} data-cy="circle-icon" />}
          </div>
        )}
        <div className={styles.mainArea}>
          <div className={styles.destination} data-cy="pickTour-destination">
            {determinePickTourType()}
          </div>
          <div className={styles.content}>
            <div className={styles.orderType}>
              <div className={styles.icon}>
                {determineIconByOrderType({
                  orderType: pickTourHasNoItems ? pickTour.pickTourType : pickTour.pickTourList[0].orderType,
                })}
              </div>
              <p data-cy="pickTour-type">
                {displayOrderType(
                  pickTourHasNoItems ? pickTour.pickTourType : pickTour.pickTourList[0].orderType,
                  pickTourDestination?.match(epRegEx) !== null ? 'Commercial - Delivery' : null,
                  pickTourDestination
                )}
              </p>
            </div>
            <div
              className={mergeClassNames(
                styles.itemsPick,
                currentLanguage === LANGUAGE_SPANISH && styles.itemsPickSpanish
              )}
            >
              <span>{t(`${translationRoot}.ITEMS_TO_PICK`)}</span>
              <span className={styles.quantity}>{pickTour.metadata.unhandledItemsRemaining}</span>
            </div>
            {pickTourOrderType === ORDER_TYPE_COMMERCIAL_EP ? (
              <CommercialEPDispatchTimeDisplay dispatchTime={getCommercialEPOrderDispatchTime()} />
            ) : (
              <div>
                <span className={styles.textLight}>{datePart}</span>
                <span className={styles.pickTourTime}>{timePart}</span>
              </div>
            )}
            <div className={styles.highPriority}>
              <span className={styles.highPriorityIcon}>
                <HighPriorityIcon />
              </span>
              <span className={styles.textLight}>{t(`${translationRoot}.HIGH_PRIORITY`)}</span>
            </div>
          </div>
        </div>
      </div>

      {isSelected && (
        <div className={styles.pickOrdersWrapper}>
          {pickTour.metadata.ordersSummaryData.map(order => (
            <div
              key={order.orderID}
              className={mergeClassNames(styles.pickOrders, isSelectiveResumeEnabled && styles.clickableOrder)}
              onClick={isSelectiveResumeEnabled ? e => toggleOrderSelection(order.orderID, e) : undefined}
            >
              {isSelectiveResumeEnabled && (
                <div className={styles.checkboxContainer}>
                  {selectedOrderIDs.has(order.orderID) ? (
                    <CheckmarkSquareIcon />
                  ) : (
                    <div className={styles.checkboxEmpty} />
                  )}
                </div>
              )}
              <span className={styles.pickOrderDestination}>
                {displayDestination({
                  orderType: pickTour.pickTourType,
                  destination: order.destination,
                })}
              </span>
              <span className={styles.textLight}>{t(`${translationRoot}.QTY`)}</span>
              <span className={styles.textLight}>{order.qty}</span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
};

export default PickTourItem;
